import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../app/components/SnackBar';
import { getApiHost } from '../../../../app/infrastructure/http/apiClient';
import i18n from '../../../../i18n/i18n.module';
import { unwrapMentorMe } from '../../application/mapMentorProfile';
import type {
  AvailableSlot,
  MentorMe,
  UpdateMentorMeRequest,
} from '../../application/types';

type PublicSlugCheck = {
  slug: string;
  available: boolean;
  reason: 'current' | 'available' | 'invalid' | 'reserved' | 'taken';
};

function authorizationHeader(token?: string | null): string | undefined {
  if (!token) return undefined;
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
}

function unwrapSlots(response: unknown): AvailableSlot[] {
  const payload =
    response && typeof response === 'object' && 'data' in response
      ? (response as { data?: unknown }).data
      : response;
  if (!Array.isArray(payload)) return [];
  return payload
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const slot = row as { startsAt?: unknown; endsAt?: unknown };
      if (slot.startsAt == null || slot.endsAt == null) return null;
      return {
        startsAt: String(slot.startsAt),
        endsAt: String(slot.endsAt),
      };
    })
    .filter((row): row is AvailableSlot => row != null);
}

function unwrapSlugCheck(response: unknown): PublicSlugCheck {
  if (response && typeof response === 'object' && 'data' in response) {
    const data = (response as { data?: PublicSlugCheck }).data;
    if (data && typeof data.available === 'boolean') return data;
  }
  return response as PublicSlugCheck;
}

function apiBaseUrl(): string {
  const host = getApiHost();
  return host.endsWith('/') ? host : `${host}/`;
}

export const mentorApi = createApi({
  reducerPath: 'mentorApi',
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl(),
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (
        getState() as { authorization?: { accessToken?: string | null } }
      ).authorization?.accessToken;
      const value = authorizationHeader(token);
      if (value) headers.set('Authorization', value);
      return headers;
    },
  }),
  tagTypes: ['MentorMe', 'MentorSlots'],
  endpoints: (build) => ({
    getMentorById: build.query<MentorMe, number>({
      query: (id) => ({
        url: `mentors/${id}`,
        method: 'GET',
      }),
      transformResponse: unwrapMentorMe,
    }),
    getMentorMe: build.query<MentorMe, void>({
      query: () => ({
        url: 'mentors/me',
        method: 'GET',
      }),
      transformResponse: unwrapMentorMe,
      providesTags: ['MentorMe'],
    }),
    updateMentorMe: build.mutation<MentorMe, UpdateMentorMeRequest | FormData>({
      query: (body) => ({
        url: 'mentors/me',
        method: 'PATCH',
        body,
      }),
      transformResponse: unwrapMentorMe,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            mentorApi.util.updateQueryData('getMentorMe', undefined, () => data),
          );
          showSuccessToast(i18n.t('mentor-profile.updated'));
        } catch {
          showErrorToast(i18n.t('mentor-profile.update-error'), undefined, 5000);
        }
      },
    }),
    publishMentorMe: build.mutation<MentorMe, void>({
      query: () => ({
        url: 'mentors/me/publish',
        method: 'POST',
      }),
      transformResponse: unwrapMentorMe,
      invalidatesTags: ['MentorMe'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showSuccessToast(i18n.t('mentor-profile.published'));
        } catch {
          showErrorToast(i18n.t('mentor-profile.publish-error'), undefined, 5000);
        }
      },
    }),
    pauseMentorMe: build.mutation<MentorMe, void>({
      query: () => ({
        url: 'mentors/me/pause',
        method: 'POST',
      }),
      transformResponse: unwrapMentorMe,
      invalidatesTags: ['MentorMe'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          showErrorToast(i18n.t('mentor-profile.status-error'), undefined, 5000);
        }
      },
    }),
    resumeMentorMe: build.mutation<MentorMe, void>({
      query: () => ({
        url: 'mentors/me/resume',
        method: 'POST',
      }),
      transformResponse: unwrapMentorMe,
      invalidatesTags: ['MentorMe'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          showErrorToast(i18n.t('mentor-profile.status-error'), undefined, 5000);
        }
      },
    }),
    getAvailableSlots: build.query<
      AvailableSlot[],
      {
        mentorId: number;
        from: string;
        to: string;
        durationMinutes: number;
      }
    >({
      query: ({ mentorId, from, to, durationMinutes }) => ({
        url: `mentors/${mentorId}/available-slots`,
        method: 'GET',
        params: { from, to, durationMinutes },
      }),
      transformResponse: unwrapSlots,
      providesTags: (_result, _error, arg) => [
        { type: 'MentorSlots', id: arg.mentorId },
      ],
    }),
    verifyMentorSlug: build.query<PublicSlugCheck, string>({
      query: (slug) => ({
        url: 'mentors/me/slug',
        method: 'GET',
        params: { slug },
      }),
      transformResponse: unwrapSlugCheck,
    }),
  }),
});

export const {
  useGetMentorByIdQuery,
  useGetMentorMeQuery,
  useUpdateMentorMeMutation,
  usePublishMentorMeMutation,
  usePauseMentorMeMutation,
  useResumeMentorMeMutation,
  useGetAvailableSlotsQuery,
  useLazyVerifyMentorSlugQuery,
} = mentorApi;
